import React, { useState, useEffect } from 'react';
import { ComplianceMatrixEntry, ComplianceMatrixSnapshot, MatrixDashboardKPIs } from './types';
import { matrixService } from './matrixService';
import { ComplianceMatrixMetricsCards } from './ComplianceMatrixMetricsCards';
import { ComplianceMatrixTable } from './ComplianceMatrixTable';
import { ComplianceMatrixDetailModal } from './ComplianceMatrixDetailModal';
import { ComplianceMatrixSnapshotModal } from './ComplianceMatrixSnapshotModal';

export const ComplianceMatrixPage: React.FC = () => {
  const [kpis, setKpis] = useState<MatrixDashboardKPIs | null>(null);
  const [entries, setEntries] = useState<ComplianceMatrixEntry[]>([]);
  const [snapshots, setSnapshots] = useState<ComplianceMatrixSnapshot[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<ComplianceMatrixEntry | null>(null);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showCreateEntryModal, setShowCreateEntryModal] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  // Create Form State
  const [newMatrixCode, setNewMatrixCode] = useState(`MAT-${Date.now().toString(36).toUpperCase()}`);
  const [newReqId, setNewReqId] = useState('CMP-REQ-001');
  const [newImplSummary, setNewImplSummary] = useState('');
  const [newDept, setNewDept] = useState('Safety & SMS');
  const [newLeadId, setNewLeadId] = useState('USR-SMS-DIR');
  const [newLeadName, setNewLeadName] = useState('Murat Kaya (Safety Manager)');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpiData, entriesData, snapshotsData] = await Promise.all([
        matrixService.getDashboardKPIs(),
        matrixService.getMatrixEntries('ORG-XYZ', {
          search: searchTerm || undefined,
          compliance_status: statusFilter || undefined,
          responsible_department: departmentFilter || undefined
        }),
        matrixService.getSnapshots()
      ]);
      setKpis(kpiData);
      setEntries(entriesData);
      setSnapshots(snapshotsData);
    } catch (err: any) {
      setError(err.message || 'Veriler yuklenirken bir hata olustu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [searchTerm, statusFilter, departmentFilter]);

  const handleCheckExpired = async () => {
    try {
      const res = await matrixService.checkExpiredEvidences();
      alert(`Kanit gecerlilik taramasi tamamlandi. Suresi dolan ${res.expired_count} adet kanit isaretlendi ve ilgili maddeler yeniden degerlendirmeye alindi.`);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCreateEntrySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await matrixService.createMatrixEntry({
        matrix_code: newMatrixCode,
        compliance_requirement_id: newReqId,
        implementation_summary: newImplSummary,
        responsible_department: newDept,
        lead_owner_id: newLeadId,
        lead_owner_name: newLeadName,
        implementation_status: 'IN_PROGRESS',
        compliance_status: 'PENDING_ASSESSMENT'
      });
      setShowCreateEntryModal(false);
      setNewImplSummary('');
      setNewMatrixCode(`MAT-${Date.now().toString(36).toUpperCase()}`);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Top Banner */}
      <div className="page-header-banner">
        <div className="badge">REGULATORY & COMPLIANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Compliance Matrix & Evidence Mapping</h1>
            <p>
              Corporate regulatory requirement verification, linked obligations and SOP bridges, audit trails, and frozen snapshots.
            </p>
          </div>
          <button
            onClick={() => setShowSnapshotModal(true)}
            className="btn-secondary"
            style={{ width: 'auto' }}
          >
            📸 Frozen Snapshots ({snapshots.length})
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-300 rounded-lg text-sm text-red-800">
          <b>Hata:</b> {error}
        </div>
      )}

      {/* Metrics Cards */}
      {kpis && (
        <ComplianceMatrixMetricsCards
          kpis={kpis}
          onRefresh={loadData}
          onCheckExpired={handleCheckExpired}
        />
      )}

      {/* Matrix Table */}
      <ComplianceMatrixTable
        entries={entries}
        onSelectEntry={(entry) => setSelectedEntry(entry)}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        departmentFilter={departmentFilter}
        onDepartmentFilterChange={setDepartmentFilter}
        onCreateEntry={() => setShowCreateEntryModal(true)}
      />

      {/* Detail Modal */}
      {selectedEntry && (
        <ComplianceMatrixDetailModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onRefresh={() => {
            loadData();
            // Reload selected entry
            matrixService.getMatrixEntryById(selectedEntry.id).then(setSelectedEntry).catch(() => {});
          }}
        />
      )}

      {/* Snapshot Modal */}
      {showSnapshotModal && (
        <ComplianceMatrixSnapshotModal
          snapshots={snapshots}
          onClose={() => setShowSnapshotModal(false)}
          onSnapshotCreated={() => {
            loadData();
          }}
        />
      )}

      {/* Create Entry Modal */}
      {showCreateEntryModal && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Add New Compliance Matrix Entry</h3>
                <p>Register compliance requirement implementation tracking</p>
              </div>
              <button type="button" onClick={() => setShowCreateEntryModal(false)} className="close-btn" aria-label="Close modal">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateEntrySubmit}>
              <div className="modal-body">
                <div className="form-field">
                  <label>Matrix Tracking Code</label>
                  <input
                    type="text"
                    value={newMatrixCode}
                    onChange={(e) => setNewMatrixCode(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Linked Regulatory Requirement</label>
                  <select
                    value={newReqId}
                    onChange={(e) => setNewReqId(e.target.value)}
                  >
                    <option value="CMP-REQ-001">CMP-ORO-GEN-200-1: Accountable Executive Safety Responsibility</option>
                    <option value="CMP-REQ-002">CMP-ORO-GEN-200-3: Proactive Safety Risk Management</option>
                    <option value="CMP-REQ-003">CMP-CAT-OP-MPA-100: Automated Flight Dispatch & Fuel Check Verification</option>
                    <option value="CMP-REQ-004">CMP-SHT-OPS-035: National Quality Management System Audit Program</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Responsible Department</label>
                  <select
                    value={newDept}
                    onChange={(e) => setNewDept(e.target.value)}
                  >
                    <option value="Safety & SMS">Safety & SMS</option>
                    <option value="Flight Operations">Flight Operations</option>
                    <option value="Quality & Compliance">Quality & Compliance</option>
                    <option value="Ground Operations">Ground Operations</option>
                  </select>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Lead Owner User ID</label>
                    <input
                      type="text"
                      value={newLeadId}
                      onChange={(e) => setNewLeadId(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Lead Owner Name & Title</label>
                    <input
                      type="text"
                      value={newLeadName}
                      onChange={(e) => setNewLeadName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Implementation & Compliance Summary</label>
                  <textarea
                    value={newImplSummary}
                    onChange={(e) => setNewImplSummary(e.target.value)}
                    rows={3}
                    placeholder="Summarize how the requirement is implemented across operations..."
                    required
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowCreateEntryModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Create Matrix Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
